import { useState } from 'react';
import Card from 'components/card';
import { MdPerson, MdPeople, MdSearch, MdGroup, MdCalendarToday } from 'react-icons/md';

interface MentorshipConnection {
  id: string;
  mentor: {
    id: string;
    name: string;
    email: string;
    avatar: string;
    skills: string[];
    totalMentees: number;
  };
  mentees: {
    id: string;
    name: string;
    email: string;
    avatar: string;
    interests: string[];
    joinDate: string;
  }[];
  startDate: string;
  status: 'active' | 'completed' | 'paused';
  type: '1:1' | '1:many';
  assignmentMethod: 'manual' | 'algorithm';
}

const MentorshipConnections = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | '1:1' | '1:many'>('all');

  // Mock mentorship connections data
  const connections: MentorshipConnection[] = [
    {
      id: '1',
      mentor: {
        id: '1',
        name: 'Dr. Sarah Chen',
        email: 'sarah.chen@university.edu',
        avatar: '/api/placeholder/40/40',
        skills: ['Machine Learning', 'Data Science', 'Python'],
        totalMentees: 2
      },
      mentees: [
        {
          id: '4',
          name: 'John Smith',
          email: 'john.smith@student.edu',
          avatar: '/api/placeholder/32/32',
          interests: ['Machine Learning', 'AI'],
          joinDate: '2025-01-15'
        },
        {
          id: '5',
          name: 'Lisa Wang',
          email: 'lisa.wang@student.edu',
          avatar: '/api/placeholder/32/32',
          interests: ['Data Science', 'Python'],
          joinDate: '2025-02-01'
        }
      ],
      startDate: '2024-01-15',
      status: 'active',
      type: '1:many',
      assignmentMethod: 'algorithm'
    },
    {
      id: '2',
      mentor: {
        id: '2',
        name: 'Jennifer Kim',
        email: 'jennifer.kim@designstudio.com',
        avatar: '/api/placeholder/40/40',
        skills: ['UX Design', 'Product Management', 'Figma'],
        totalMentees: 1
      },
      mentees: [
        {
          id: '6',
          name: 'Sophie Chen',
          email: 'sophie.chen@student.edu',
          avatar: '/api/placeholder/32/32',
          interests: ['UX Design', 'User Research'],
          joinDate: '2025-03-20'
        }
      ],
      startDate: '2024-01-20',
      status: 'active',
      type: '1:1',
      assignmentMethod: 'manual'
    },
    {
      id: '3',
      mentor: {
        id: '7',
        name: 'Prof. Maria Garcia',
        email: 'maria.garcia@chemistry.edu',
        avatar: '/api/placeholder/40/40',
        skills: ['Organic Chemistry', 'Research Methods', 'Laboratory Management'],
        totalMentees: 3
      },
      mentees: [
        {
          id: '8',
          name: 'Alex Rodriguez',
          email: 'alex.rodriguez@student.edu',
          avatar: '/api/placeholder/32/32',
          interests: ['Organic Chemistry', 'Research'],
          joinDate: '2023-09-15'
        },
        {
          id: '9',
          name: 'Emma Thompson',
          email: 'emma.thompson@student.edu',
          avatar: '/api/placeholder/32/32',
          interests: ['Laboratory Techniques', 'Chemical Analysis'],
          joinDate: '2023-10-01'
        },
        {
          id: '10',
          name: 'David Wilson',
          email: 'david.wilson@student.edu',
          avatar: '/api/placeholder/32/32',
          interests: ['Research Methods', 'Data Analysis'],
          joinDate: '2023-11-10'
        }
      ],
      startDate: '2023-09-15',
      status: 'completed',
      type: '1:many',
      assignmentMethod: 'algorithm'
    },
    {
      id: '4',
      mentor: {
        id: '9',
        name: 'Dr. Ahmed Khan',
        email: 'ahmed.khan@physics.edu',
        avatar: '/api/placeholder/40/40',
        skills: ['Theoretical Physics', 'Quantum Mechanics', 'Research'],
        totalMentees: 1
      },
      mentees: [
        {
          id: '11',
          name: 'Rachel Green',
          email: 'rachel.green@student.edu',
          avatar: '/api/placeholder/32/32',
          interests: ['Quantum Physics', 'Theoretical Research'],
          joinDate: '2025-06-01'
        }
      ],
      startDate: '2024-03-01',
      status: 'paused',
      type: '1:1',
      assignmentMethod: 'manual'
    }
  ];

  // Filter connections
  const filteredConnections = connections.filter(connection => {
    const matchesSearch = 
      connection.mentor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.mentor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.mentees.some(mentee => 
        mentee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mentee.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesType = typeFilter === 'all' || connection.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  const getTypeColor = (type: string) => {
    return type === '1:1'
      ? 'bg-brand-100 text-brand-800 dark:bg-brand-900/20 dark:text-brand-300'
      : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
  };

  return (
    <div className="mt-3 h-full w-full">
      <Card extra="w-full pb-10 p-6 h-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10">
              <MdGroup className="h-6 w-6 text-brand-500" />
            </div>
            <div>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                Existing Connections
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Overview of all mentorship connections
              </p>
            </div>
          </div>

          {/* Search and Type Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search mentors or mentees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

            {/* Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
            >
              <option value="all">All Types</option>
              <option value="1:1">One to One</option>
              <option value="1:many">One to Many</option>
            </select>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500">
                <MdPerson className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400">One to One</p>
                <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                  {connections.filter(r => r.type === '1:1').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500">
                <MdPeople className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-purple-600 dark:text-purple-400">One to Many</p>
                <p className="text-xl font-bold text-purple-700 dark:text-purple-300">
                  {connections.filter(r => r.type === '1:many').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-500">
                <MdGroup className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-xl font-bold text-gray-700 dark:text-gray-300">
                  {connections.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Connections List */}
        <div className="space-y-6">
          {filteredConnections.length === 0 ? (
            <div className="text-center py-12">
              <MdGroup className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">
                No connections found
              </h3>
              <p className="text-gray-500 dark:text-gray-500">
                {searchTerm || typeFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'No mentorship connections have been established yet'
                }
              </p>
            </div>
          ) : (
            filteredConnections.map((connection) => (
              <div
                key={connection.id}
                className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                {/* Connection Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    {/* Mentor Avatar */}
                    <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <MdPerson className="h-6 w-6 text-brand-600" />
                    </div>
                    
                    {/* Mentor Info */}
                    <div>
                      <h5 className="text-lg font-semibold text-navy-700 dark:text-white">
                        {connection.mentor.name}
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {connection.mentor.email}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {connection.mentor.skills.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Type Badge */}
                  <div className="flex flex-col gap-2">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(connection.type)}`}>
                      {connection.type === '1:1' ? 'One to One' : 'One to Many'}
                    </span>
                  </div>
                </div>

                {/* Mentees with Connection Details */}
                <div className="mt-4 pl-14">
                  <h6 className="font-medium text-navy-700 dark:text-white mb-3">
                    Mentees ({connection.mentees.length})
                  </h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {connection.mentees.map((mentee) => (
                      <div key={mentee.id} className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <MdPerson className="h-3 w-3 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-navy-700 dark:text-white text-xs truncate">
                              {mentee.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {mentee.email}
                            </p>
                          </div>
                        </div>
                        
                        {/* Mentee Interests */}
                        <div className="mb-2">
                          <div className="flex flex-wrap gap-1">
                            {mentee.interests.slice(0, 2).map((interest, index) => (
                              <span
                                key={index}
                                className="inline-flex px-1 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                              >
                                {interest}
                              </span>
                            ))}
                            {mentee.interests.length > 2 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                +{mentee.interests.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Connection Details for this mentee */}
                        <div className="space-y-1 pt-2 border-t border-blue-200 dark:border-blue-800">
                          <div className="flex items-center gap-1 text-xs">
                            <MdCalendarToday className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">Joined:</span>
                            <span className="text-navy-700 dark:text-white font-medium">
                              {new Date(mentee.joinDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-1 text-xs">
                            <MdPeople className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">Type:</span>
                            <span className="text-navy-700 dark:text-white font-medium">
                              {connection.assignmentMethod === 'manual' ? 'Manual' : 'Algorithm'}
                            </span>
                          </div>

                          <div className="flex items-center gap-1 text-xs">
                            <MdCalendarToday className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">Since:</span>
                            <span className="text-navy-700 dark:text-white font-medium">
                              {new Date(mentee.joinDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default MentorshipConnections;